import { useEffect, useMemo, useRef, useState } from "react";

import { useRootDispatch, useRootSelector } from "store/hooks";
import {
  getPaymentMethods,
  selectedAccountInfo,
} from "store/account/account-selector";

import { AddAch } from "./addAch/AddAch";
import { CreditCard } from "./creditCard/CreditCard";
import { MakePaymentClick } from "components/sliderPage/makePayment/MakePayment";
import LoadTranslation from "components/common/LoadTranslation";

import { setCurrentPaymentMethod } from "store/common/common-slice";
import { ACH, CARD, EWALLET } from "constants/common";
import { EWalletSelector } from "../eWalletSelector/EWalletSelector";

import classes from "./AddPaymentMethods.module.scss";
import { useTranslation } from "react-i18next";
import CardForm from "./Card/CardForm";
import { PaymentGateway } from "store/config/ewallet-configuration";
import { hasAccountPermission } from "utility/account";
import { AccountMatrix } from "store/account/account-state";
import { getEffectiveEWalletConfiguration } from "utility";
export enum PaymentActionType {
  /**
   * This type is used when you need to make the payment immediately
   * Usually this is used when you need to enter a new card details
   * and make the payment using that.
   */
  PAY = "PAY",
  /**
   * This type is used when you need to save the card
   * this saved card is used for future auto payment methods
   */
  SAVE = "SAVE",
  /**
   * This is used when you need to make the payment in the next step
   * or may be towards the end of the entire cycle.
   */
  PAY_LATER = "PAY_LATER",
  /**
   * This is used when we need to SAVE the payment method and also
   * PAY at a later step(not immediately).
   * SAVE step is also dependent on the user selecting the "Save card"
   * checkbox, meaning SAVE will be ignored if not checked and will behave
   * exactly like PAY_LATER
   */
  SAVE_AND_PAY_LATER = "SAVE_AND_PAY_LATER",
}

export interface AddPaymentMethodsProps {
  inlineView?: boolean;
  buttonClick?: MakePaymentClick;
  updateButtonClick?: (
    updateFn: (prev: MakePaymentClick) => MakePaymentClick,
  ) => void;
  paymentHandler?: Function;
  paymentAmount?: number;
  paymentActionType: PaymentActionType;
  borderView?: boolean;
  selectedWalletType?: number | null;
  setSelectedWalletType?: (walletType: number) => void;
}

export const AddPaymentMethods: React.FC<AddPaymentMethodsProps> = ({
  inlineView,
  buttonClick,
  updateButtonClick,
  paymentHandler,
  paymentAmount,
  paymentActionType,
  borderView,
  selectedWalletType,
  setSelectedWalletType,
}) => {
  const { ewallet } = useRootSelector((state) => state.config);
  const { pageDetail, currentPaymentMethod } = useRootSelector(
    (state) => state.common,
  );
  const accountInfo = useRootSelector(selectedAccountInfo);
  const { t } = useTranslation(["common"]);
  const editMode = pageDetail?.editMode ? pageDetail.editMode : false;

  const [selectedTab, setSelectedTab] = useState<number>(0);

  const tabsRef: any = useRef();

  const hasACH = hasAccountPermission(
    accountInfo?.clientAttributes,
    AccountMatrix.EnableACH,
  );
  const hasCard = hasAccountPermission(
    accountInfo?.clientAttributes,
    AccountMatrix.EnableCard,
  );

  const hasEWallet = hasAccountPermission(
    accountInfo?.clientAttributes,
    AccountMatrix.EnableEWallet,
  );

  const paymentMethods = useRootSelector(getPaymentMethods);

  const allTabs = Object.keys(paymentMethods);

  let tabs: string[] = useMemo(() => {
    return allTabs.filter((tab) => {
      if (!hasACH && tab === ACH) return false;
      if (!hasCard && tab === CARD) return false;
      if (!hasEWallet && tab === EWALLET) return false;
      return true;
    });
  }, [allTabs, hasACH, hasCard, hasEWallet]);

  const dispatch = useRootDispatch();

  useEffect(() => {
    const temp = tabsRef.current;
    temp.addEventListener("MDCTabBar:activated", (e: any) => {
      setSelectedTab(e.detail.index);
    });
    return () => {
      temp.removeEventListener("MDCTabBar:activated", (e: any) => {
        setSelectedTab(e.detail.index);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const tab = tabs[selectedTab];
    if (tab && currentPaymentMethod !== tab) {
      dispatch(setCurrentPaymentMethod(tab));
    }
  }, [selectedTab, tabs, dispatch, currentPaymentMethod]);

  const effectiveConfig = useMemo(
    () => getEffectiveEWalletConfiguration(ewallet, accountInfo?.accountType),
    [ewallet, accountInfo?.accountType],
  );

  const renderTab = () => {
    let comp = null;
    const tab = tabs[selectedTab];
    switch (tab) {
      case ACH:
        comp = (
          <AddAch
            buttonClick={buttonClick}
            paymentHandler={paymentHandler}
            action={paymentActionType}
          />
        );
        break;
      case CARD:
        //Check for SlimCD or Authorize.net - both use CardForm, default back to Tsys for now
        if (
          effectiveConfig.paymentGateway === PaymentGateway.SlimCD ||
          effectiveConfig.paymentGateway === PaymentGateway.AuthorizeNet
        ) {
          comp = (
            <CardForm
              buttonClick={buttonClick}
              paymentHandler={paymentHandler}
              updateButtonClick={updateButtonClick}
              action={paymentActionType}
              effectiveConfig={effectiveConfig}
            />
          );
        } else {
          comp = (
            <CreditCard
              buttonClick={buttonClick}
              updateButtonClick={updateButtonClick}
              paymentHandler={paymentHandler}
              editMode={editMode}
              action={paymentActionType}
            />
          );
        }
        break;
      case EWALLET:
        comp = (
          <EWalletSelector
            providers={paymentMethods[EWALLET]?.walletProviders || []}
            paymentHandler={paymentHandler}
            paymentAmount={paymentAmount}
            action={paymentActionType}
            buttonClick={buttonClick}
            updateButtonClick={updateButtonClick}
            selectedWalletType={selectedWalletType}
            setSelectedWalletType={setSelectedWalletType}
          />
        );
        break;
      default:
        break;
    }
    return comp;
  };

  const renderTabs = () => {
    return tabs.map((single) => {
      return (
        <ft-tab
          key={single}
          noRipple
          label={t(`common:${single}_label`)}
        ></ft-tab>
      );
    });
  };

  let pageClass = [classes.PageWrapper];
  if (inlineView) {
    pageClass.push(classes.InlineView);
  }
  if (borderView) {
    pageClass.push(classes.BorderView);
  }

  return (
    <div className={pageClass.join(" ")}>
      <div className={classes.HeadWrapper}>
        <div className={classes.ModuleHeadWrapper}>
          <ft-tab-bar activeIndex={selectedTab} ref={tabsRef} noRipple>
            {renderTabs()}
          </ft-tab-bar>
        </div>
      </div>
      <div className={classes.ModuleWrapper}>{renderTab()}</div>
      <LoadTranslation keys={["payment_final"]} />
    </div>
  );
};
